interface ToastPayload {
  variant: "default" | "destructive" | "success";
  description: string;
  duration: number;
}

const DEFAULT_DURATION: number = 3000;

export const SECTOR_EMAILS_TOASTS = {
  CREATED: (): ToastPayload => ({
    variant: "default",
    description: "Destinatário adicionado com sucesso",
    duration: DEFAULT_DURATION,
  }),
  UPDATED: (): ToastPayload => ({
    variant: "default",
    description: "Destinatário atualizado com sucesso",
    duration: DEFAULT_DURATION,
  }),
  DELETED: (): ToastPayload => ({
    variant: "default",
    description: "Destinatário removido das listas operacionais",
    duration: DEFAULT_DURATION,
  }),
  ACTIVE_CHANGED: (active: boolean): ToastPayload => ({
    variant: "default",
    description: active
      ? "Destinatário reativado — voltará a receber os envios"
      : "Destinatário inativado — envios pausados",
    duration: DEFAULT_DURATION,
  }),
  EXPORT_REQUESTED: (filtered: number, total: number): ToastPayload => ({
    variant: "default",
    description: `Exportando ${filtered} de ${total} contatos (filtrados)`,
    duration: DEFAULT_DURATION,
  }),
  FAILED: (description: string): ToastPayload => ({
    variant: "destructive",
    description,
    duration: DEFAULT_DURATION,
  }),
} as const;

export const SECTOR_EMAILS_ERRORS = {
  CREATE: "Não foi possível adicionar o destinatário",
  UPDATE: "Não foi possível atualizar o destinatário",
  CHANGE_STATUS: "Não foi possível alterar o status do destinatário",
  DELETE: "Não foi possível excluir o destinatário",
} as const;

export const SECTOR_EMAILS_LABELS = {
  EXPORT_FALLBACK: "erro inesperado ao gerar a planilha",
  PAGE_TITLE: "E-mails de Setores",
  PAGE_DESCRIPTION:
    "Gerencie os destinatários por setor para envio automático de planilhas e aprovações.",
  SEARCH_PLACEHOLDER: "Buscar por setor, nome, email, envio...",
  FILTERS: "Filtros",
  FILTER_SECTOR: "Selecionar setor",
  FILTER_SHIPPING_TYPE: "Selecionar tipo de envio",
  FILTER_STATUS: "Selecionar status",
  ACTIVE_FILTERS: (count: number): string =>
    count === 1 ? "1 filtro ativo" : `${count} filtros ativos`,
  CLEAR_FILTERS: "Limpar filtros",
  APPLY_FILTERS: "Aplicar",
  EXPORT_EXCEL: "Exportar Excel",
  ADD_RECIPIENT: "Adicionar Destinatário",
  EDIT_RECIPIENT: "Editar Destinatário",
  DELETE_RECIPIENT: "Excluir Destinatário",
  HISTORY: "Histórico de alterações e envios",
  DEACTIVATE: "Inativar",
  REACTIVATE: "Reativar",
  EDIT: "Editar",
  DELETE: "Excluir",
  CANCEL: "Cancelar",
  SAVE: "Salvar",
  ACTIVE_HINT: "Ativo (recebendo envios)",
  INACTIVE_HINT: "Inativo (envios pausados — ex: férias)",
  EMPTY_STATE: "Nenhum destinatário encontrado com os filtros aplicados",
  ROWS_PER_PAGE: "Linhas por página",
  PAGE_OF: (current: number, last: number): string =>
    `Página ${current} de ${last}`,
  RESULT_COUNT: (total: number): string =>
    `${total} destinatário(s) encontrado(s).`,
  FIELD_SECTOR: "Setor",
  FIELD_NAME: "Nome do Destinatário",
  FIELD_NAME_HINT: "Para identificação no histórico",
  FIELD_EMAIL: "E-mail Corporativo",
  FIELD_SHIPPING_TYPES: "Tipo de Envio",
  REQUIRED_MARK: "*",
  SECTOR_PLACEHOLDER: "Selecione o setor",
  INVALID_DOMAIN: "Domínio inválido. Utilize um e-mail @grupoavenida.com.br",
  SECTOR_REQUIRED: "Selecione o setor",
  EMAIL_REQUIRED: "Informe o e-mail corporativo",
  SHIPPING_TYPES_REQUIRED: "Selecione ao menos um tipo de envio",
  FISCAL_PERIOD_NOTE:
    "Período fiscal mensal: dia 26 do mês anterior ao dia 25 do mês corrente. Consolidada Desconto em Folha é autoatendimento via a tela de Aprovação de Vales RH (sem envio automático).",
  AUDIT_NOTE:
    "Todas as ações passadas ficam registradas no histórico com as informações vigentes no momento. As alterações aplicam-se apenas para envios futuros.",
  DELETE_EXPLANATION:
    "Excluir remove o destinatário das listas operacionais (saída definitiva, ex: desligamento). As informações ficam salvas no banco de dados (histórico de envios anteriores preservado para auditoria).",
  DELETE_PAUSE_HINT:
    "Para pausa temporária (férias/licença), use o checkbox Ativo da listagem.",
  HISTORY_EMPTY: "Nenhuma alteração registrada para este destinatário",
} as const;

export const CRON_TEST_LABELS = {
  TITLE: "Disparo manual dos crons (somente homologação)",
  DESCRIPTION:
    "Enfileira na shipping-test-queue o mesmo código que o cron roda: a geração das 23h do dia 25 e o envio das 08h do dia 1. A data de referência é derivada do ciclo informado, não do relógio — é o que faz o envio encontrar o ciclo que você acabou de gerar. Acompanhe também pelo Bull Board.",
  CYCLE_LABEL: "Ciclo fiscal",
  CYCLE_PLACEHOLDER: "AAAA-MM",
  CYCLE_INVALID: "Informe o ciclo no formato AAAA-MM",
  GENERATE: "Gerar planilhas",
  DISPATCH: "Enviar e-mails",
  RESET: "Limpar ciclo",
  CANCEL: "Cancelar",
  CONFIRM: "Confirmar",
  CLEAR_JOB: "Limpar",
  JOB_SUMMARY: (jobId: string, action: string, state: string): string =>
    `Job #${jobId} · ${action} · ${state}`,
  SNAPSHOT_STATE: (hasSnapshot: boolean): string =>
    hasSnapshot
      ? "Snapshot do ciclo existe — para gerar de novo, limpe o ciclo antes."
      : "Sem snapshot para este ciclo.",
  COLUMN_TYPE: "Planilha",
  COLUMN_SENT: "Envio",
  COLUMN_ATTEMPTS: "Tentativas",
  COLUMN_DELIVERIES: "Entregas",
  SENT_AT: (iso: string): string =>
    `Enviado ${new Date(iso).toLocaleString("pt-BR")}`,
  BLOCKED: "Bloqueado",
  NOT_SENT: "Nao enviado",
  DELIVERY_COUNTS: (delivered: number, failed: number, total: number): string =>
    `${delivered} ok · ${failed} falha(s) · ${total} total`,
  DISPATCH_TITLE: "Disparar o envio?",
  DISPATCH_DESCRIPTION: (cycle: string): string =>
    `O ciclo ${cycle} passa pelo envio real, com anexo. Fora de producao o e-mail nao chega ao destinatario cadastrado: vai todo para o endereco de desenvolvimento, com assunto prefixado "(HML) [→ destinatario-real]". O que se testa aqui e o caminho completo, nao a entrega ao destinatario final.`,
  RESET_TITLE: "Limpar o ciclo?",
  RESET_DESCRIPTION: (cycle: string): string =>
    `O snapshot e os despachos do ciclo ${cycle} serao apagados em definitivo, junto com as entregas registradas. E o que permite gerar o mesmo ciclo de novo, ja que o snapshot e imutavel por design.`,
} as const;
